import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components, DropdownIndicatorProps } from 'react-select';

const DropdownIndicator = (
  props: DropdownIndicatorProps<ColourOption, true>
) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.25519 10.9424C8.31213 11.5671 7.18123 11.9308 5.96537 11.9308C2.67079 11.9308 0 9.26 0 5.9654C0 2.6708 2.67079 0 5.96537 0C9.25995 0 11.9307 2.6708 11.9307 5.9654C11.9307 7.18123 11.567 8.3121 10.9424 9.25514L13.8253 12.138C14.0582 12.371 14.0582 12.7487 13.8253 12.9816L12.9816 13.8253C12.7487 14.0582 12.371 14.0582 12.138 13.8253L9.25519 10.9424ZM9.54459 5.9654C9.54459 7.94216 7.94212 9.54465 5.96537 9.54465C3.98862 9.54465 2.38615 7.94216 2.38615 5.9654C2.38615 3.98864 3.98862 2.38616 5.96537 2.38616C7.94212 2.38616 9.54459 3.98864 9.54459 5.9654Z"
          fill="#9CA3AF"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

interface Location {
  name: string;
  code: string;
}

interface CountryInputProps {
  inputError: boolean;
  setLocation?: React.Dispatch<React.SetStateAction<Location> | undefined>;
  countries: {
    name: string;
    code: string;
  }[];
  location?: Location;
}

const CountryInput: React.FC<CountryInputProps> = props => {
  const { setLocation, inputError, countries, location } = props;
  const [validInput, setValidInput] = useState<boolean>(false);

  const { t } = useTranslation();

  useEffect(() => {
    setValidInput(inputError && location?.name === '');
  }, [inputError, location]);

  const styles = {
    menuList: styles => ({
      ...styles,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'rgba(99, 102, 241)' : 'inherit',
      '&:hover': {
        backgroundColor: state.isSelected ? 'rgba(99, 102, 241)' : '#f1f5f9',
      },
      fontSize: 14,
      fontWeight: '600',
      paddingLeft: '1.5rem',
    }),
    menu: base => ({
      ...base,
      zIndex: 100,
    }),
    control: (base, state) => ({
      ...base,
      flexDirection: 'row-reverse',
      minHeight: '40px',
      border: validInput
        ? '1px solid red'
        : state.isFocused
        ? '1px solid rgba(99, 102, 241,1)'
        : '1px solid rgba(203, 213, 225,1)',
      boxShadow: state.isFocused
        ? '0 0 0 0px #fff,0 0 0 calc(4px + 0px) rgba(224, 231, 225,1),0 0 #0000'
        : 'none',
      '&:hover': {
        border: '1px solid rgba(107, 114, 128,1)',
      },
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      marginLeft: '0.5rem',
    }),
    placeholder: defaultStyles => ({
      ...defaultStyles,
      color: 'rgba(203, 213, 225,1) !important',
      fontSize: 14,
      fontWeight: '500',
    }),
  };

  return (
    <div className="w-full">
      <Select
        components={{
          DropdownIndicator,
          IndicatorSeparator: () => null,
        }}
        className={cn(
          'country-selector',
          inputError && location?.name === '' ? 'input-error' : ''
        )}
        placeholder={t('Search')}
        defaultValue={countries[0]?.name}
        options={countries}
        getOptionLabel={option => option['name']}
        getOptionValue={option => option['code']}
        onChange={e => {
          setLocation && setLocation({ name: e['name'], code: e['code'] });
        }}
        styles={styles}
        theme={theme => ({
          ...theme,
          borderRadius: 8,
          colors: {
            ...theme.colors,
            primary25: 'rgba(203, 213, 225,1)',
          },
        })}
      />
    </div>
  );
};

export default CountryInput;
